import { Link } from "wouter";
import "./login.css";

/**
 * The landing spot for a refused/failed SSO round trip (ADR-0037 §UX edge
 * states). `reason` selects copy only — never data that could distinguish
 * "this tenant doesn't have SSO" from "this subject was never invited" from
 * "this org doesn't exist" (the server's own refusal is deliberately
 * identical across all three, ADR-0037 §4; this page must not reintroduce a
 * distinction the server went out of its way to remove).
 *
 * ⚠ NOT YET REACHABLE via the real server flow as of ADR-0037 PR-2b — see
 * this PR's notes. `ssosvc.CallbackHandler` today answers refusals (403 "not
 * permitted"), a deactivated account (403 "deactivated"), and an unreachable
 * IdP (502/429) by writing the error body DIRECTLY to the top-level GET
 * navigation response — the browser shows raw JSON, never routing back into
 * the SPA. This page is built and ready, but the server needs to redirect
 * (302, same pattern as `postLoginRedirect`'s success case — a FIXED path,
 * never built from request input, so it stays free of open-redirect risk) to
 * a path like `/sso/error?reason=refused|deactivated|unavailable` on every
 * refusal path instead of writing the body directly.
 */
export type SSOErrorReason = "refused" | "deactivated" | "unavailable";

const COPY: Record<SSOErrorReason, { title: string; description: string }> = {
  // Non-leaky by construction (ADR-0037 §4): identical whether the org
  // doesn't exist, SSO isn't configured for it, or this identity was never
  // invited. Never branch this copy on anything more specific.
  refused: {
    title: "Couldn't sign you in",
    description:
      "We weren't able to sign you in with that identity. If you believe this is a mistake, check with whoever manages your organization's Comebye account.",
  },
  deactivated: {
    title: "This account is deactivated",
    description: "Your account has been deactivated. Contact your organization's admin to restore access.",
  },
  unavailable: {
    title: "Sign-in is temporarily unavailable",
    description: "We couldn't reach your organization's identity provider. Try again shortly.",
  },
};

function parseReason(): SSOErrorReason {
  const raw = new URLSearchParams(window.location.search).get("reason");
  return raw === "deactivated" || raw === "unavailable" ? raw : "refused";
}

export function SSOErrorPage() {
  const { title, description } = COPY[parseReason()];

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="login__brand-mark" aria-hidden="true" />
          <span className="login__brand-name">Comebye</span>
        </div>
        <p className="login__subtitle" role="alert">
          {title}
        </p>
        <p className="login__note">{description}</p>
        <Link to="/login" className="login__note">
          Back to sign-in
        </Link>
      </div>
    </div>
  );
}
